import { useNavigate } from 'react-router-dom';
import { useV2Auth } from '@/context/V2AuthContext';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryAction, SecondaryAction } from '@/components/mask/MaskSystem';
import '@/components/mask/mask.css';

/* Startseite CarmaCircle 2.0 — Mensch → Mensch. Ruhig, direkt, ohne Community-Sprache. */

function RotatingImpulse({ impulses }) {
  const [index, setIndex] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % impulses.length);
        setFading(false);
      }, 600);
    }, 6000);
    return () => clearInterval(interval);
  }, [impulses.length]);

  return (
    <p
      className={`mask-headline text-xl sm:text-2xl text-[var(--text)]/80 leading-relaxed transition-all duration-500 ${
        fading ? 'opacity-0 translate-y-1' : 'opacity-100 translate-y-0'
      }`}
      data-testid="rotating-impulse"
    >
      {impulses[index]}
    </p>
  );
}

export default function LandingV2() {
  const { user } = useV2Auth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const impulses = t('v2.landing.impulses', { returnObjects: true });
  const promise = t('v2.landing.promise', { returnObjects: true });
  const steps = t('v2.landing.steps', { returnObjects: true });

  const goGive = () => navigate('/give');
  const goFuerDich = () => navigate(user ? '/inbox' : '/auth');

  return (
    <div className="relative min-h-screen overflow-x-hidden text-[var(--text)]" data-testid="landing-page">
      {/* HERO */}
      <section className="relative min-h-[78vh] flex flex-col">
        <div className="mask-bg-word" aria-hidden="true">
          <span style={{ transform: 'translateX(-6%)' }}>{t('v2.bgWords.carma')}</span>
        </div>
        <div className="relative z-10 flex-1 flex flex-col justify-center w-full max-w-2xl mx-auto px-6 sm:px-8 py-20">
          <p className="mask-enter text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-6">CarmaCircle</p>
          <h1 className="mask-enter mask-enter-delay-1 mask-headline text-4xl sm:text-5xl lg:text-6xl text-[var(--text)] mb-7">
            {t('v2.landing.heroTitle')}
          </h1>
          <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] leading-relaxed max-w-md mb-12">
            {t('v2.landing.heroText')}
          </p>
          <div className="mask-enter mask-enter-delay-3 flex flex-col sm:flex-row sm:items-center gap-3">
            <PrimaryAction onClick={goGive} data-testid="hero-cta-give">{t('v2.common.giveCarma')}</PrimaryAction>
            {user && <SecondaryAction onClick={goFuerDich} data-testid="hero-cta-inbox">{t('v2.landing.ctaFuerdich')}</SecondaryAction>}
          </div>
        </div>
      </section>

      {/* IMPULS */}
      <section className="w-full max-w-2xl mx-auto px-6 sm:px-8 py-20" data-testid="landing-impulse">
        <div className="min-h-[6rem] flex items-center">
          <RotatingImpulse impulses={impulses} />
        </div>
        <button
          type="button"
          onClick={goGive}
          data-testid="impulse-cta-give"
          className="mt-6 text-sm text-[var(--muted)] hover:text-[var(--text)] underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors"
        >
          {t('v2.common.giveCarma')}
        </button>
      </section>

      {/* DAS VERSPRECHEN */}
      <section className="w-full max-w-2xl mx-auto px-6 sm:px-8 py-20" data-testid="landing-promise">
        <div className="divide-y divide-[var(--border)]/30">
          {promise.map((p) => (
            <div key={p.title} className="py-8">
              <h3 className="mask-headline text-2xl sm:text-3xl text-[var(--text)] mb-2.5">{p.title}</h3>
              <p className="text-sm text-[var(--muted)] leading-relaxed max-w-md">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SO FUNKTIONIERT ES */}
      <section className="w-full max-w-2xl mx-auto px-6 sm:px-8 py-20" data-testid="landing-how">
        <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-12">{t('v2.landing.how')}</p>
        <div className="space-y-14">
          {steps.map((step, i) => (
            <div key={step.title} className="flex items-start gap-6">
              <span className="text-xs tracking-[0.2em] text-[var(--muted)] pt-2 shrink-0">{String(i + 1).padStart(2, '0')}</span>
              <div>
                <h3 className="mask-headline text-xl sm:text-2xl text-[var(--text)] mb-2">{step.title}</h3>
                <p className="text-sm text-[var(--muted)] leading-relaxed max-w-md">{step.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ABSCHLUSS */}
      <section className="relative w-full max-w-2xl mx-auto px-6 sm:px-8 pt-24 pb-32" data-testid="landing-closing">
        <p className="mask-headline text-2xl sm:text-3xl text-[var(--text)]/90 leading-relaxed mb-4">
          {t('v2.landing.closing1')}
        </p>
        <p className="text-base text-[var(--muted)] mb-12">{t('v2.landing.closing2')}</p>
        <PrimaryAction onClick={goGive} data-testid="bottom-cta">{t('v2.common.giveCarma')}</PrimaryAction>
      </section>
    </div>
  );
}
